"use client";
import {
  useComboboxList
} from "../__chunks/SKM4ZW3Y.js";
import {
  useComboboxProviderContext
} from "../__chunks/N4X7J4OQ.js";
import "../__chunks/QFI3MCMR.js";
import "../__chunks/6LTHW2TQ.js";
import {
  usePopover
} from "../__chunks/LTYEFENI.js";
import {
  createDialogComponent
} from "../__chunks/IOUGWXY5.js";
import "../__chunks/GOXKZAQI.js";
import "../__chunks/6GXEOXGT.js";
import "../__chunks/PTPUIM7N.js";
import "../__chunks/4ZRXV5C7.js";
import "../__chunks/U2L67A6K.js";
import "../__chunks/4KKLZMKS.js";
import "../__chunks/HSFE2URL.js";
import "../__chunks/H2NVVTJB.js";
import "../__chunks/YNKDLCEK.js";
import "../__chunks/MKDDWKFK.js";
import "../__chunks/5QL6BP6J.js";
import "../__chunks/OOBDFMJL.js";
import "../__chunks/53CQGCDZ.js";
import "../__chunks/677M2CI3.js";
import "../__chunks/UHSH3H5I.js";
import "../__chunks/UYRJLDVS.js";
import "../__chunks/JPQNKTGF.js";
import "../__chunks/LQTCWTYB.js";
import "../__chunks/7452U3HH.js";
import "../__chunks/XPF5GU3Q.js";
import "../__chunks/4MFOHMLS.js";
import "../__chunks/KCMTTTFR.js";
import "../__chunks/5B3U64HV.js";
import "../__chunks/IWHI6Y45.js";
import "../__chunks/RG6J4TID.js";
import "../__chunks/7JCFLN3D.js";
import "../__chunks/SHA3WOPI.js";
import "../__chunks/JG4BJLXB.js";
import {
  createElement,
  createHook,
  forwardRef
} from "../__chunks/GUCZNEAD.js";
import "../__chunks/2KACNUKS.js";
import "../__chunks/ZIX5D53S.js";
import "../__chunks/PWUXK5IM.js";
import "../__chunks/WOWUAP42.js";
import "../__chunks/XM66DUTO.js";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "../__chunks/4R3V3JGP.js";

// src/combobox/combobox-popover.tsx
import { useRef } from "react";
import { getDocument, matches } from "@ariakit/core/utils/dom";
import { invariant, isFalsyBooleanCallback } from "@ariakit/core/utils/misc";
var TagName = "div";
function isController(target, ...ids) {
  if (!target)
    return false;
  if ("id" in target) {
    const selector = ids.filter(Boolean).map((id) => `[aria-controls~="${id}"]`).join(", ");
    if (!selector)
      return false;
    return matches(target, selector);
  }
  return false;
}
var useComboboxPopover = createHook(
  function useComboboxPopover2(_a) {
    var _b = _a, {
      store,
      modal,
      tabIndex,
      alwaysVisible,
      autoFocusOnHide = true,
      hideOnInteractOutside = true
    } = _b, props = __objRest(_b, [
      "store",
      "modal",
      "tabIndex",
      "alwaysVisible",
      "autoFocusOnHide",
      "hideOnInteractOutside"
    ]);
    const context = useComboboxProviderContext();
    store = store || context;
    invariant(
      store,
      process.env.NODE_ENV !== "production" && "ComboboxPopover must receive a `store` prop or be wrapped in a ComboboxProvider component."
    );
    const baseElement = store.useState("baseElement");
    const hiddenByClickOutsideRef = useRef(false);
    props = useComboboxList(__spreadValues({ store, alwaysVisible }, props));
    props = usePopover(__spreadProps(__spreadValues({
      store,
      modal,
      alwaysVisible,
      backdrop: false,
      autoFocusOnShow: false,
      finalFocus: baseElement,
      preserveTabOrderAnchor: null
    }, props), {
      // When the combobox popover is modal, we make sure to include the
      // combobox input and all the combobox controls (cancel, disclosure) in
      // the list of persistent elements so they make part of the modal context,
      // allowing users to tab through them.
      getPersistentElements() {
        var _a2;
        const elements = ((_a2 = props.getPersistentElements) == null ? void 0 : _a2.call(props)) || [];
        if (!modal)
          return elements;
        if (!store)
          return elements;
        const { contentElement, baseElement: baseElement2 } = store.getState();
        if (!baseElement2)
          return elements;
        const doc = getDocument(baseElement2);
        const selectors = [];
        if (contentElement == null ? void 0 : contentElement.id) {
          selectors.push(`[aria-controls~="${contentElement.id}"]`);
        }
        if (baseElement2 == null ? void 0 : baseElement2.id) {
          selectors.push(`[aria-controls~="${baseElement2.id}"]`);
        }
        if (!selectors.length)
          return [...elements, baseElement2];
        const selector = selectors.join(",");
        const controlElements = doc.querySelectorAll(selector);
        return [...elements, ...controlElements];
      },
      // The combobox popover should focus on the combobox input when it hides,
      // unless the event was triggered by a click outside the popover, in which
      // case the input shouldn't be re-focused.
      autoFocusOnHide(element) {
        if (isFalsyBooleanCallback(autoFocusOnHide, element))
          return false;
        if (hiddenByClickOutsideRef.current) {
          hiddenByClickOutsideRef.current = false;
          return false;
        }
        return true;
      },
      // Make sure we don't hide the popover when the user interacts with the
      // combobox cancel or the combobox disclosure buttons. They will have the
      // aria-controls attribute pointing to either the combobox input or the
      // combobox popover elements.
      hideOnInteractOutside(event) {
        var _a2, _b2;
        const state = store == null ? void 0 : store.getState();
        const contentId = (_a2 = state == null ? void 0 : state.contentElement) == null ? void 0 : _a2.id;
        const baseId = (_b2 = state == null ? void 0 : state.baseElement) == null ? void 0 : _b2.id;
        if (isController(event.target, contentId, baseId))
          return false;
        const result = typeof hideOnInteractOutside === "function" ? hideOnInteractOutside(event) : hideOnInteractOutside;
        if (result) {
          hiddenByClickOutsideRef.current = event.type === "click";
        }
        return result;
      }
    }));
    return props;
  }
);
var ComboboxPopover = createDialogComponent(
  forwardRef(function ComboboxPopover2(props) {
    const htmlProps = useComboboxPopover(props);
    return createElement(TagName, htmlProps);
  }),
  useComboboxProviderContext
);
export {
  ComboboxPopover,
  useComboboxPopover
};
